import { useEffect, useState } from "react";
import { DATE_FORMAT, formatDate, getBrowserTimezone } from "src/utils/date-utils";
import Styles from "./GameClock.module.scss";

interface GameClockProps {
  // Epoch ms of the game's first pitch — the clock's anchor (the local browser time it was thrown).
  firstPitchMs: number;
  // Epoch ms of the last pitch; the elapsed reference once the game is final. Ignored while live.
  lastPitchMs?: number | null;
  isLive?: boolean;
}

function formatElapsed(ms: number, withSeconds: boolean): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => `${n}`.padStart(2, "0");
  if (withSeconds) return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/**
 * First-pitch time + elapsed game clock, anchored to the first pitch (NOT the scheduled start). The
 * first-pitch time is shown in the browser's local timezone; the clock counts up from it — ticking
 * every second while the game is live, frozen at the last pitch (total game length) once final.
 */
export function GameClock({ firstPitchMs, lastPitchMs, isLive }: GameClockProps) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!isLive) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isLive]);

  const firstPitchLabel = formatDate(new Date(firstPitchMs), getBrowserTimezone(), DATE_FORMAT.time);
  const end = isLive ? now : lastPitchMs ?? now;
  const elapsed = formatElapsed(end - firstPitchMs, !!isLive);

  return (
    <div className={Styles.clock} title="Elapsed time since the first pitch">
      <span className={Styles.label}>First pitch</span>
      <span className={Styles.value}>{firstPitchLabel}</span>
      <span className={Styles.dot} />
      <span className={Styles.elapsed}>{elapsed}</span>
    </div>
  );
}
